import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Configuration for rotating text behavior.
 */
export type RotatingTextConfig = {
	/** Pool of texts to rotate through. */
	texts: string[];
	/** Delay in ms before the first rotation begins. Default: 3000 */
	initialDelayMs?: number;
	/** Interval in ms between each rotation. Default: 1500 */
	intervalMs?: number;
};

export type UseRotatingTextResult = {
	/** The currently displayed text. */
	text: string;
	/**
	 * Imperatively restart the rotation cycle.
	 * Clears all timers, resets to the first text in the pool,
	 * and re-starts the initial delay before rotating again.
	 * Stable identity — safe to omit from dependency arrays.
	 */
	reset: () => void;
};

const DEFAULT_INITIAL_DELAY_MS = 3000;
const DEFAULT_INTERVAL_MS = 1800;

/**
 * Fisher-Yates shuffle — returns a new shuffled copy of the array.
 */
function shuffle<T>(array: T[]): T[] {
	const result = [...array];
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
}

/**
 * General-purpose hook that displays the first item from `texts` for an
 * initial delay, then cycles through the remaining items at a fixed interval.
 *
 * When the pool is exhausted it reshuffles and starts again.
 *
 * Exposes a stable `reset()` callback that imperatively restarts the full
 * cycle (initial delay → rotation) without requiring a key change.
 *
 * @example
 * ```ts
 * const { text, reset } = useRotatingText({
 *   texts: ['Thinking…', 'Searching…', 'Connecting dots…'],
 *   initialDelayMs: 3000,
 *   intervalMs: 1500,
 * });
 *
 * // Imperative reset (e.g. when a new question is sent)
 * useEffect(() => { reset(); }, [messageId, reset]);
 * ```
 */
export function useRotatingText(config: RotatingTextConfig): UseRotatingTextResult {
	const {
		texts,
		initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
		intervalMs = DEFAULT_INTERVAL_MS,
	} = config;

	const initialText = texts[0] ?? '';

	const [currentText, setCurrentText] = useState(initialText);

	// ── refs for mutable state that must survive across closures ──────────
	const poolRef = useRef<string[]>([]);
	const indexRef = useRef(0);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Keep latest config values in refs so `startCycle` always reads fresh
	// values without needing to be recreated on every config change.
	const textsRef = useRef(texts);
	textsRef.current = texts;

	const initialDelayRef = useRef(initialDelayMs);
	initialDelayRef.current = initialDelayMs;

	const intervalMsRef = useRef(intervalMs);
	intervalMsRef.current = intervalMs;

	const initialTextRef = useRef(initialText);
	initialTextRef.current = initialText;

	// ── timer helpers ─────────────────────────────────────────────────────
	const clearTimers = useCallback(() => {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	/**
	 * Starts the full cycle:
	 * 1. Show `initialText` immediately.
	 * 2. After `initialDelayMs`, build a shuffled pool and begin rotating.
	 */
	const startCycle = useCallback(() => {
		clearTimers();

		const currentTexts = textsRef.current;
		setCurrentText(initialTextRef.current);
		poolRef.current = [];
		indexRef.current = 0;

		if (currentTexts.length <= 1) {
			return;
		}

		timeoutRef.current = setTimeout(() => {
			// Build initial shuffled pool (excluding the first/initial text to avoid repetition)
			poolRef.current = shuffle(currentTexts.slice(1));
			indexRef.current = 0;

			// Show first rotating text immediately
			setCurrentText(poolRef.current[0]);

			intervalRef.current = setInterval(() => {
				indexRef.current += 1;

				// Rebuild pool when exhausted
				if (indexRef.current >= poolRef.current.length) {
					poolRef.current = shuffle(textsRef.current.slice(1));
					indexRef.current = 0;
				}

				setCurrentText(poolRef.current[indexRef.current]);
			}, intervalMsRef.current);
		}, initialDelayRef.current);
	}, [clearTimers]);

	// ── start on mount, clean up on unmount ───────────────────────────────
	useEffect(() => {
		startCycle();
		return clearTimers;
	}, [startCycle, clearTimers]);

	// ── public API ────────────────────────────────────────────────────────
	// `reset` has a stable identity (only depends on `startCycle` which only
	// depends on `clearTimers` — both produced by useCallback with [] deps).
	const reset = startCycle;

	return { text: currentText, reset };
}
